// Minimum-characters and mandatory more-info rules for the legacy payer survey. Three layers carry
// the same rules and have to agree; the counterparts are named inline where they differ in shape:
//   - api/misc/models.py                      _below_min_chars, _json_column_*, Response._validate_*
//   - payer-portal utils/min-chars.ts         isBelowMinChars
//   - payer-portal utils/mandatory-columns.ts effectiveMandatoryColumns
//   - payer-portal QuestionValidatorService   the Angular twin of this file
// A rule changed here almost always has to change in the others too. One divergence is sanctioned:
// the backend exempts an already-submitted response from the per-column mandatory rule, and no client
// implements that - the amend screens refuse a cleared box the API would accept.
//
// Closure-free helpers live at module scope (javascript:S7721). Prefixed to avoid colliding with
// other files in the same bundle.
const MCH_RATIONALE_FIELDS = ['rationale', 'rationale1', 'rationale2'];
const MCH_PAYER_RATIONALE_FIELDS = ['payer_rationale', 'payer_rationale1', 'payer_rationale2'];
const MCH_VW_KEYS = ['acceptable', 'expensive', 'too_expensive', 'prohibitive'];
const MCH_MANDATORY_RATIONALE_MESSAGE = 'Rationales are required for all options';

// Coerced, not assumed to be a string: a TOR2 cell holds a boolean or an array for non-textbox types.
function mchCount(value) {
  return String(value ?? '').trim().length;
}

// A minimum constrains content, not presence: a blank box never fails it.
function mchIsBelowMin(value, min) {
  if (!min || min <= 0) { return false; }
  const length = mchCount(value);
  return length > 0 && length < min;
}

function mchIsBlank(value) {
  return mchCount(value) === 0;
}

function mchMessage(min) {
  return 'Please enter at least ' + min + ' characters.';
}

function mchRationaleMessage(min) {
  return 'Please enter at least ' + min + ' characters in the \'More Information\' box.';
}

// Per-column config is stored either as an array or as a sparse index-keyed dict. mchJsonColumnLen
// mirrors the backend's _json_column_len. mchJsonColumnValue is the raw read that the backend
// duplicates inside BOTH _json_column_value and _json_column_flag - do not give it their guards, it
// has to pass booleans through for mchIsColumnMandatory. The guarded counterparts are
// mchJsonColumnCount (of _json_column_value) and mchIsColumnMandatory (of _json_column_flag).
function mchJsonColumnLen(config) {
  if (Array.isArray(config)) { return config.length; }
  if (config && typeof config === 'object') {
    const indexes = Object.keys(config).filter(key => /^\d+$/.test(key)).map(Number);
    return indexes.length ? Math.max(...indexes) + 1 : 0;
  }
  return 0;
}

function mchJsonColumnValue(config, index) {
  if (!config) { return null; }
  return config[index] ?? config[String(index)] ?? null;
}

// Minimums only: the backend's _json_column_value keeps a stored value only when it is a plain
// integer, so anything else (a string "50", a bool, a nested container) must not be enforced here
// either - the server would ignore it.
function mchJsonColumnCount(config, index) {
  const value = mchJsonColumnValue(config, index);
  return Number.isInteger(value) ? value : null;
}

function mchRationaleField(index) {
  return MCH_RATIONALE_FIELDS[index] ?? MCH_RATIONALE_FIELDS[0];
}

// The API may send these enums as strings; normalise before comparing, handling the same
// string-vs-number API quirk as portalHelper.isSingleRationale.
function mchRationaleType(question) {
  return Number(question.rationale_type);
}

function mchQuestionType(question) {
  return Number(question.type);
}

function mchAllowPayerOption(question) {
  return Number(question.allow_payer_option);
}

// numberOfMoreInfoColumns only exists after layoutService.transformQuestions, and legacy scales leave
// more_info_columns_count null - fall back to the config array's own length, as the backend does.
function mchColumnCount(question, config) {
  const configured = question.scale?.numberOfMoreInfoColumns
    || question.scale?.more_info_columns_count
    || mchJsonColumnLen(config);
  return Math.min(configured || 1, MCH_RATIONALE_FIELDS.length);
}

function mchColumnMin(question, index) {
  return mchJsonColumnCount(question.scale?.rationale_min_chars, index);
}

function mchIsColumnMandatory(config, index) {
  return mchJsonColumnValue(config, index) === true;
}

// Any stored config - including an empty or non-container one - means "only the columns it flags are
// mandatory"; the question-level flag is consulted only when nothing is stored at all. Mirrors the
// backend's Response._mandatory_rationale_columns.
function mchMandatoryColumns(question) {
  const configured = question.scale?.rationale_mandatory;
  if (configured != null) {
    return mchJsonColumnLen(configured) > 0 ? configured : null;
  }
  if (!question.scale || !question.rationale_mandatory) { return null; }
  return new Array(mchColumnCount(question, null)).fill(true);
}

function mchRationaleFailure(min, selector) {
  return {message: mchRationaleMessage(min), selector: selector};
}

function mchMandatoryFailure(selector) {
  return {message: MCH_MANDATORY_RATIONALE_MESSAGE, selector: selector};
}

// Van Westendorp renders its own per-tier inline message, so its failures need no selector. Flagged so
// callers can tell "no selector needed" from "a selector was expected and could not be resolved".
function mchSelfHighlightingFailure(min) {
  return {message: mchRationaleMessage(min), selector: null, selfHighlighting: true};
}

// RADIO_RANKING rows are keyed by rating-scale option id and every other type by question-option id,
// so a row key has to be resolved against both collections - the union, not one or the other. A
// RADIO_RANKING question that also carries question_options (retyped from checkboxes, say) would
// otherwise never match a single ranking row, and every failure would come back with no selector.
// Same resolution authoring makes in get_scale_or_question_options.
function mchRowOptions(question) {
  return [...(question.options ?? []), ...(question.scale?.options ?? [])];
}

function mchIsRegularRowKey(question, rowKey) {
  return mchRowOptions(question).some(option => String(option?.id) === String(rowKey));
}

// Two shapes: a regular row is keyed by its option id and renders `option-<option.id>-<field>`, while a
// multi-other row is keyed by the payer's free-text option name and renders
// `option-<other_index>-<question.id>-<field>`. other_options is consulted first so a payer who types
// "12" into an Other box resolves to their own row. That ordering does not fix the underlying defect:
// rating_responses["12"] and rating_responses[12] are one and the same property, so a payer-typed name
// that equals a real option id collides on a single row and neither ordering can tell them apart.
// A payer-typed key is never interpolated either way: some throw in Sizzle ("a:b[c]!"), and the rest
// are worse - a plain name like Aspirin parses fine and silently matches nothing.
function mchRowSelector(question, response, rowKey, field) {
  const otherIndex = (response.other_options ?? [])
    .findIndex(option => option?.option_name === rowKey);
  if (otherIndex >= 0) {
    return '#question-input-' + question.id + ' .option-' + otherIndex + '-' + question.id + '-' + field;
  }
  if (mchIsRegularRowKey(question, rowKey)) {
    return '#question-input-' + question.id + ' .option-' + rowKey + '-' + field;
  }
  return null;
}

function mchPayerColumnSelector(question, index) {
  return '#question-input-' + question.id + ' .payer-option-rationale-' + index;
}

// The next two scanners return a column index, or -1. Keeping the per-column loops out of the
// callers is what holds those callers under the cognitive-complexity limit (javascript:S3776).
function mchFirstBlankMandatoryColumn(source, fields, mandatory, total) {
  for (let i = 0; i < total; i++) {
    if (mchIsColumnMandatory(mandatory, i) && mchIsBlank(source[fields[i]])) { return i; }
  }
  return -1;
}

function mchFirstUnmetColumn(question, source, fields, total) {
  for (let i = 0; i < total; i++) {
    if (mchIsBelowMin(source[fields[i]], mchColumnMin(question, i))) { return i; }
  }
  return -1;
}

// The four builders below wrap those scanners and return a {message, selector} failure, or null.
// A row whose key matches neither an option id nor an other_options entry yields a null selector.
// It is still reported: the key is in the response, so it is sent and the API rejects it anyway, and
// a client-side message beats a whole-survey rejection. pshAddInputErrorClass reports the miss.
function mchMandatoryRowFailure(question, response, rows, mandatory, total) {
  for (const rowKey in rows ?? {}) {
    const row = rows[rowKey];
    if (!row?.value) { continue; }
    const column = mchFirstBlankMandatoryColumn(row, MCH_RATIONALE_FIELDS, mandatory, total);
    if (column < 0) { continue; }
    return mchMandatoryFailure(mchRowSelector(question, response, rowKey, mchRationaleField(column)));
  }
  return null;
}

function mchMandatoryPayerFailure(question, response, mandatory, total) {
  const column = mchFirstBlankMandatoryColumn(response, MCH_PAYER_RATIONALE_FIELDS, mandatory, total);
  return column >= 0 ? mchMandatoryFailure(mchPayerColumnSelector(question, column)) : null;
}

function mchMinCharsRowFailure(question, response, rows, total) {
  for (const rowKey in rows ?? {}) {
    const row = rows[rowKey];
    if (!row?.value) { continue; }
    const column = mchFirstUnmetColumn(question, row, MCH_RATIONALE_FIELDS, total);
    if (column < 0) { continue; }
    const selector = mchRowSelector(question, response, rowKey, mchRationaleField(column));
    return mchRationaleFailure(mchColumnMin(question, column), selector);
  }
  return null;
}

function mchMinCharsPayerFailure(question, response, total) {
  const column = mchFirstUnmetColumn(question, response, MCH_PAYER_RATIONALE_FIELDS, total);
  if (column < 0) { return null; }
  return mchRationaleFailure(mchColumnMin(question, column), mchPayerColumnSelector(question, column));
}

app.factory('minCharsHelper', ['CONSTANTS', function (CONSTANTS) {
  const helper = {};

  helper.count = mchCount;
  helper.isBlank = mchIsBlank;
  helper.isBelowMin = mchIsBelowMin;
  helper.message = mchMessage;
  helper.rationaleMessage = mchRationaleMessage;

  function isColumnFormat(question) {
    const type = mchQuestionType(question);
    return type === CONSTANTS.questionTypeIds.RATING || type === CONSTANTS.questionTypeIds.RADIO_RANKING;
  }

  // Mirrors the backend's Response._has_per_column_mandatory_config. A stored per-column config is
  // authoritative for a column-format question, so payerSurveyHelper's older question-level sweep has
  // to stand aside for the same shapes the server stands aside for - it only ever inspects column 0,
  // and would otherwise block a blank column 0 that the server and the payer portal both accept.
  // firstMandatoryError below then enforces the config, per column and on rated rows only.
  helper.hasPerColumnMandatoryConfig = function (question) {
    return isColumnFormat(question) && question.scale?.rationale_mandatory != null;
  };

  // The SINGLE payer-added option's own rationales live on the Response. The row counts as answered
  // from the scale value alone - the typed label can be cleared afterwards - as it does on the
  // backend (permit_payer_option and payer_value is not None).
  function singleOtherRowRated(question, response) {
    return mchAllowPayerOption(question) === CONSTANTS.otherOptions.SINGLE_OTHER
      && response.payer_value != null;
  }

  function columnRows(question, response) {
    return (mchQuestionType(question) === CONSTANTS.questionTypeIds.RATING)
      ? response.rating_responses
      : response.ranking_responses;
  }

  function checkResponseValue(question, response) {
    if (mchQuestionType(question) !== CONSTANTS.questionTypeIds.FREE_TEXT) { return null; }
    if (!mchIsBelowMin(response.response_value, question.response_min_chars)) { return null; }
    return {
      message: mchMessage(question.response_min_chars),
      selector: '#MAT_Response_Question_' + question.id
    };
  }

  function checkSingleRationale(question, response) {
    if (mchRationaleType(question) !== CONSTANTS.rationaleTypes.SINGLE) { return null; }
    if (!mchIsBelowMin(response.rationale, question.rationale_min_chars)) { return null; }
    return mchRationaleFailure(
      question.rationale_min_chars,
      '#MainContent_rpt_QuestionsGroups_rpt_Questions_rationaleBox_single_' + question.id
    );
  }

  // RATING / RADIO_RANKING: mandatory more-info columns on rated rows, mirroring the backend's
  // Response._validate_per_column_rationale_mandatory.
  helper.firstMandatoryError = function (question, response) {
    if (!question || !response) { return null; }
    if (mchRationaleType(question) !== CONSTANTS.rationaleTypes.MULTIPLE) { return null; }
    if (!isColumnFormat(question)) { return null; }

    const mandatory = mchMandatoryColumns(question);
    if (!mandatory) { return null; }
    const total = mchColumnCount(question, mandatory);

    const rowFailure = mchMandatoryRowFailure(
      question, response, columnRows(question, response), mandatory, total);
    if (rowFailure) { return rowFailure; }

    if (!singleOtherRowRated(question, response)) { return null; }
    return mchMandatoryPayerFailure(question, response, mandatory, total);
  };

  // RATING / RADIO_RANKING: per-column minimums on rows the payer answered.
  function checkPerColumn(question, response) {
    if (!isColumnFormat(question)) { return null; }

    const total = mchColumnCount(question, question.scale?.rationale_min_chars);

    const rowFailure = mchMinCharsRowFailure(question, response, columnRows(question, response), total);
    if (rowFailure) { return rowFailure; }

    if (!singleOtherRowRated(question, response)) { return null; }
    return mchMinCharsPayerFailure(question, response, total);
  }

  // CHECK_BOXES / POINTS_ALLOCATION / SLIDER: one minimum per option row, on engaged rows.
  function engagedRowRationale(question, response, option) {
    const id = String(option.id);
    const type = mchQuestionType(question);
    if (type === CONSTANTS.questionTypeIds.CHECK_BOXES) {
      const row = response.checkbox_responses?.[id];
      return row?.checked ? row.rationale : null;
    }
    if (type === CONSTANTS.questionTypeIds.POINTS_ALLOCATION) {
      const row = response.points_allocation_responses?.[id];
      return row?.points != null ? row.rationale : null;
    }
    const row = response.rating_responses?.[id];
    const touched = row?.value != null || row?.non_discrete_scale_value != null;
    return touched ? row.rationale : null;
  }

  function checkPerOption(question, response) {
    const rowFormat = [
      CONSTANTS.questionTypeIds.CHECK_BOXES,
      CONSTANTS.questionTypeIds.POINTS_ALLOCATION,
      CONSTANTS.questionTypeIds.SLIDER_RATING_SCALE
    ];
    if (!rowFormat.includes(mchQuestionType(question))) { return null; }

    for (const option of question.options ?? []) {
      const value = engagedRowRationale(question, response, option);
      if (value == null) { continue; }
      if (mchIsBelowMin(value, option.rationale_min_chars)) {
        return mchRationaleFailure(option.rationale_min_chars,
          '#question-input-' + question.id + ' .option-' + option.id + '-rationale');
      }
    }
    return null;
  }

  function otherRowEngaged(question, response) {
    const type = mchQuestionType(question);
    if (type === CONSTANTS.questionTypeIds.CHECK_BOXES) {
      return !!response.payer_option?.trim();
    }
    if (type === CONSTANTS.questionTypeIds.POINTS_ALLOCATION) {
      return response.payer_points != null;
    }
    if (type === CONSTANTS.questionTypeIds.SLIDER_RATING_SCALE) {
      return response.payer_value != null || response.non_discrete_payer_value != null;
    }
    return false;
  }

  function checkOtherRow(question, response) {
    if (mchAllowPayerOption(question) !== CONSTANTS.otherOptions.SINGLE_OTHER) { return null; }
    if (!otherRowEngaged(question, response)) { return null; }
    if (!mchIsBelowMin(response.payer_rationale, question.payer_option_rationale_min_chars)) { return null; }
    return mchRationaleFailure(question.payer_option_rationale_min_chars,
      mchPayerColumnSelector(question, 0));
  }

  // No selector by design: vanWestendorpPayerView shows its own per-tier message from
  // ui_error_rationales, so the payer already sees which tier failed.
  function checkVanWestendorp(question, response) {
    if (mchQuestionType(question) !== CONSTANTS.questionTypeIds.VAN_WESTENDORP_PRICING_MODEL) { return null; }
    const answers = response.van_westendorp_responses;
    if (!answers) { return null; }

    for (const key of MCH_VW_KEYS) {
      if (answers[key] == null || answers[key] === '') { continue; }
      const min = question.pricing_model?.['rationale_min_chars_' + key];
      if (mchIsBelowMin(answers['rationale_' + key], min)) {
        return mchSelfHighlightingFailure(min);
      }
    }
    return null;
  }

  // First rationale failure for a question. Mandatory columns are reported before minimums, matching the
  // order the backend validates them in.
  // Minimums never fail a blank box - presence is the mandatory flag's business.
  helper.firstError = function (question, response) {
    if (!question || !response) { return null; }

    const checks = [checkResponseValue, checkSingleRationale];
    if (mchRationaleType(question) === CONSTANTS.rationaleTypes.MULTIPLE) {
      checks.push(helper.firstMandatoryError, checkPerColumn, checkOtherRow, checkPerOption, checkVanWestendorp);
    }

    for (const check of checks) {
      const failure = check(question, response);
      if (failure) { return failure; }
    }
    return null;
  };

  return helper;
}]);
